import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

export function Services() {
  return (
    <div data-testid="services-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-16">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">Services</p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance max-w-5xl text-[#0F1E3C]"
        >
          Everything you need to hire
          <br />
          <span className="italic font-serif gradient-text-amber">the right way.</span>
        </motion.h1>
        <p className="mt-7 text-lg text-[#475569] max-w-2xl">
          From permanent placement to on-demand contractors, advisory to executive search —
          one partner, six disciplines, deep expertise across IT and non-IT roles.
        </p>
      </section>

      <section className="container-x pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ServiceCard
            number="01"
            title="Direct Hire Staffing"
            description="Permanent talent placement for roles that define your business. We source, vet, and deliver candidates who stay."
            p1="Retained & contingent search"
            p2="Cultural fit scoring"
            p3="6-month placement guarantee"
            testId="service-row-direct-hire"
            delay={0}
          />
          <ServiceCard
            number="02"
            title="Staff Augmentation"
            description="On-demand engineers and specialists who integrate with your team from day one, billed hourly or by project."
            p1="W2 and 1099 contractors"
            p2="Scalable team expansion"
            p3="Same-week onboarding"
            testId="service-row-staff-augmentation"
            delay={0.05}
          />
          <ServiceCard
            number="03"
            title="Executive Search"
            description="Discreet, high-touch search for director, VP, and C-suite leaders across IT, engineering, and operations."
            p1="Confidential outreach"
            p2="Leadership assessment"
            p3="Global candidate network"
            testId="service-row-executive-search"
            delay={0.1}
          />
          <ServiceCard
            number="04"
            title="Contract Staffing"
            description="Short and long-term contract talent for project surges, seasonal demand, and specialized builds."
            p1="W2 & C2C models"
            p2="Compliance handled"
            p3="Contract-to-hire option"
            testId="service-row-contract-staffing"
            delay={0.15}
          />
          <ServiceCard
            number="05"
            title="Technical Recruitment"
            description="Deep-domain recruiters who understand the difference between Spring Boot and Spring MVC, PLC and DCS."
            p1="Tech screen included"
            p2="Niche skill sourcing"
            p3="Engineering-led interviews"
            testId="service-row-technical-recruitment"
            delay={0.2}
          />
          <ServiceCard
            number="06"
            title="IT Consulting"
            description="Advisory engagements for hiring strategy, talent benchmarking, compensation studies, and workforce planning."
            p1="Org design reviews"
            p2="Market salary reports"
            p3="Hiring playbooks"
            testId="service-row-it-consulting"
            delay={0.25}
          />
        </div>
      </section>

      <section className="bg-white border-y border-[#EAE3D2] py-20 sm:py-24">
        <div className="container-x">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">Our process</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F1E3C] mb-14 max-w-3xl">
            Five steps from requisition to first-day-on-the-job.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <ProcessStep step="01" title="Discover" description="Deep intake on the role, team, and success criteria." testId="process-step-0" delay={0} />
            <ProcessStep step="02" title="Source" description="Targeted outreach across our network and proprietary database." testId="process-step-1" delay={0.08} />
            <ProcessStep step="03" title="Vet" description="Technical screens, reference checks, cultural calibration." testId="process-step-2" delay={0.16} />
            <ProcessStep step="04" title="Submit" description="Curated shortlist with notes — typically within 72 hours." testId="process-step-3" delay={0.24} />
            <ProcessStep step="05" title="Close" description="Offer coaching, onboarding, and 90-day check-ins." testId="process-step-4" delay={0.32} />
          </div>
        </div>
      </section>

      <section className="container-x section-y">
        <div className="rounded-3xl p-10 sm:p-16 bg-[#0F1E3C] text-white relative overflow-hidden">
          <div className="blob-amber top-0 right-0 opacity-50" />
          <div className="relative z-10 max-w-3xl">
            <h3 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mb-5 text-balance">
              Ready to start?
            </h3>
            <p className="text-lg text-white/70 mb-8">
              Share your role, your timeline, and what&rsquo;s not working with your current
              pipeline. We&rsquo;ll come back within 24 hours with a plan.
            </p>
            <Link to="/contact" data-testid="services-cta-contact" className="btn-accent">
              Start a search <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ number, title, description, p1, p2, p3, testId, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay, ease }}
      data-testid={testId}
      className="card-soft rounded-2xl p-8 transition-all duration-300 group"
    >
      <div className="flex items-center justify-between mb-5">
        <span className="text-xs font-semibold text-[#E9610E] tracking-wider">{number}</span>
        <ArrowUpRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#E9610E] transition-colors" />
      </div>
      <h3 className="font-display font-bold text-2xl text-[#0F1E3C] mb-3">{title}</h3>
      <p className="text-base text-[#475569] leading-relaxed mb-6">{description}</p>
      <ul className="space-y-2.5">
        <li className="flex items-start gap-2 text-sm text-[#334155]">
          <Check className="w-4 h-4 text-[#E9610E] mt-0.5 shrink-0" />
          <span>{p1}</span>
        </li>
        <li className="flex items-start gap-2 text-sm text-[#334155]">
          <Check className="w-4 h-4 text-[#E9610E] mt-0.5 shrink-0" />
          <span>{p2}</span>
        </li>
        <li className="flex items-start gap-2 text-sm text-[#334155]">
          <Check className="w-4 h-4 text-[#E9610E] mt-0.5 shrink-0" />
          <span>{p3}</span>
        </li>
      </ul>
    </motion.div>
  );
}

function ProcessStep({ step, title, description, testId, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease }}
      className="rounded-2xl p-6 bg-[#FAF7F2] border border-[#EAE3D2]"
      data-testid={testId}
    >
      <div className="text-sm font-bold text-[#E9610E] mb-3">{step}</div>
      <h4 className="font-display font-bold text-lg text-[#0F1E3C] mb-2">{title}</h4>
      <p className="text-sm text-[#475569] leading-relaxed">{description}</p>
    </motion.div>
  );
}
